import React, { useEffect, useState, Component } from 'react';
import emailjs from '@emailjs/browser';
import { Upload, FileText, X, CheckCircle, AlertCircle, ArrowLeft, Mail, Briefcase, User, Phone } from 'lucide-react';
export function UploadDocs() {
  const [formData, setFormData] = useState({
    email: '',
    businessName: '',
    fullName: '',
    phone: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [prefillUrl, setPrefillUrl] = useState('');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email') || '';
    const business = params.get('business') || '';
    const name = params.get('name') || '';
    const phone = params.get('phone') || '';
    setFormData({
      email: decodeURIComponent(email),
      businessName: decodeURIComponent(business),
      fullName: decodeURIComponent(name),
      phone: decodeURIComponent(phone)
    });
    const baseUrl = window.location.origin + '/upload-docs';
    const urlParams = new URLSearchParams();
    if (email) urlParams.set('email', email);
    if (business) urlParams.set('business', business);
    if (name) urlParams.set('name', name);
    if (phone) urlParams.set('phone', phone);
    const query = urlParams.toString();
    setPrefillUrl(query ? `${baseUrl}?${query}` : baseUrl);
  }, []);
  const validateEmail = (email: string): boolean => {
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return regex.test(email);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setErrors(prev => ({
      ...prev,
      files: ''
    }));
    if (files.length > 3) {
      setErrors(prev => ({
        ...prev,
        files: 'You can upload up to 3 files only'
      }));
      return;
    }
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    files.forEach(file => {
      const isValidType = file.type === 'application/pdf' || file.type === 'image/jpeg' || file.type === 'image/jpg';
      const isValidSize = file.size <= 10 * 1024 * 1024;
      if (!isValidType) {
        invalidFiles.push(`${file.name}: Invalid file type (PDF or JPG only)`);
      } else if (!isValidSize) {
        invalidFiles.push(`${file.name}: File too large (max 10 MB)`);
      } else {
        validFiles.push(file);
      }
    });
    if (invalidFiles.length > 0) {
      setErrors(prev => ({
        ...prev,
        files: invalidFiles.join('; ')
      }));
    }
    setUploadedFiles(validFiles);
  };
  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    if (uploadedFiles.length === 0) {
      newErrors.files = 'Please select at least one file to upload';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setUploadStatus('loading');
    try {
      const serviceId = 'service_otq5unq';
      const templateId = 'template_docs_fasttrack';
      const publicKey = 'GAU9a8WUfs7tCBUMg';
      const fileData = await Promise.all(uploadedFiles.map(async file => ({
        name: file.name,
        data: await convertFileToBase64(file)
      })));
      const templateParams: Record<string, string> = {
        to_email: 'antony@sparrowadvance.com',
        business_name: formData.businessName,
        full_name: formData.fullName || 'Not provided',
        email: formData.email,
        phone: formData.phone || 'Not provided',
        subject: `DOCS RECEIVED — ${formData.businessName} — Bank Statements`
      };
      if (fileData[0]) {
        templateParams.file1_name = fileData[0].name;
        templateParams.file1_data = fileData[0].data;
      }
      if (fileData[1]) {
        templateParams.file2_name = fileData[1].name;
        templateParams.file2_data = fileData[1].data;
      }
      if (fileData[2]) {
        templateParams.file3_name = fileData[2].name;
        templateParams.file3_data = fileData[2].data;
      }
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      setUploadStatus('success');
    } catch (error) {
      console.error('Error uploading documents:', error);
      setUploadStatus('error');
    }
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(prefillUrl);
  };
  return <div className="w-full min-h-screen bg-gradient-to-b from-gray-50 to-white" data-id="element-289">
      <header className="sticky top-0 z-50 bg-white shadow-md" data-id="element-290">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-291">
          <div className="flex justify-between items-center h-20" data-id="element-292">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uLtTqiE6RVpVibQKkcS3sU/unnamed.png" alt="Sparrow Advance" className="h-14 sm:h-16 w-auto" data-id="element-293" />
            <span className="text-sm sm:text-base font-semibold text-[#142B44]" data-id="element-294">
              Secure Upload
            </span>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-295">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden" data-id="element-296">
          <div className="bg-gradient-to-r from-[#142B44] to-[#2D5D9F] px-4 sm:px-6 lg:px-8 py-4 sm:py-5" data-id="element-297">
            <div className="flex items-center justify-center" data-id="element-298">
              <Upload className="text-white mr-2" size={24} data-id="element-299" />
              <span className="text-white font-bold text-lg" data-id="element-300">
                Fast-Track Document Upload
              </span>
            </div>
          </div>
          <div className="p-6 sm:p-8 lg:p-10" data-id="element-301">
            {uploadStatus === 'success' ? <div className="text-center space-y-6" data-id="element-302">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full" data-id="element-303">
                  <CheckCircle className="text-green-600" size={48} data-id="element-304" />
                </div>
                <div data-id="element-305">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#142B44] mb-3" data-id="element-306">
                    ✅ Documents Received
                  </h2>
                  <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-307">
                    A funding specialist will review them today.
                  </p>
                  <p className="text-sm text-gray-600 italic" data-id="element-308">
                    If you had trouble, email them to{' '}
                    <a href="mailto:antony@sparrowadvance.com" className="text-[#2D5D9F] underline" data-id="element-309">
                      antony@sparrowadvance.com
                    </a>{' '}
                    with subject: Fast-Track — {formData.businessName}
                  </p>
                </div>
                <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all" data-id="element-310">
                  <ArrowLeft className="mr-2" size={18} data-id="element-311" />
                  Return to Application
                </a>
              </div> : <>
                <div className="text-center mb-8" data-id="element-312">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#142B44] mb-3" data-id="element-313">
                    Upload Your 3 Most Recent Business Bank Statements
                  </h1>
                  <p className="text-sm text-gray-600 font-medium" data-id="element-314">
                    Secure upload • PDF or bank-export JPG (no camera photos) •
                    Max 10 MB each • (Se habla español)
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-315">
                  <div data-id="element-316">
                    <label className="block text-sm font-bold text-[#142B44] mb-2 uppercase tracking-wide" data-id="element-317">
                      Email *
                    </label>
                    <div className="relative" data-id="element-318">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-319" />
                      <input type="email" required value={formData.email} onChange={e => {
                    setFormData({
                      ...formData,
                      email: e.target.value
                    });
                    if (errors.email) {
                      setErrors({
                        ...errors,
                        email: ''
                      });
                    }
                  }} disabled={uploadStatus === 'loading'} className={`w-full pl-12 pr-4 py-4 border-2 ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium disabled:bg-gray-100 disabled:cursor-not-allowed`} placeholder="john@company.com" data-id="element-320" />
                    </div>
                    {errors.email && <p className="mt-2 text-sm text-red-600" data-id="element-321">
                        {errors.email}
                      </p>}
                  </div>
                  <div data-id="element-322">
                    <label className="block text-sm font-bold text-[#142B44] mb-2 uppercase tracking-wide" data-id="element-323">
                      Business Name *
                    </label>
                    <div className="relative" data-id="element-324">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-325" />
                      <input type="text" required value={formData.businessName} onChange={e => {
                    setFormData({
                      ...formData,
                      businessName: e.target.value
                    });
                    if (errors.businessName) {
                      setErrors({
                        ...errors,
                        businessName: ''
                      });
                    }
                  }} disabled={uploadStatus === 'loading'} className={`w-full pl-12 pr-4 py-4 border-2 ${errors.businessName ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium disabled:bg-gray-100 disabled:cursor-not-allowed`} placeholder="ABC Company LLC" data-id="element-326" />
                    </div>
                    {errors.businessName && <p className="mt-2 text-sm text-red-600" data-id="element-327">
                        {errors.businessName}
                      </p>}
                  </div>
                  <div data-id="element-328">
                    <label className="block text-sm font-bold text-[#142B44] mb-2 uppercase tracking-wide" data-id="element-329">
                      Full Name
                    </label>
                    <div className="relative" data-id="element-330">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-331" />
                      <input type="text" value={formData.fullName} onChange={e => setFormData({
                    ...formData,
                    fullName: e.target.value
                  })} disabled={uploadStatus === 'loading'} className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="John Doe" data-id="element-332" />
                    </div>
                  </div>
                  <div data-id="element-333">
                    <label className="block text-sm font-bold text-[#142B44] mb-2 uppercase tracking-wide" data-id="element-334">
                      Phone
                    </label>
                    <div className="relative" data-id="element-335">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-336" />
                      <input type="tel" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} disabled={uploadStatus === 'loading'} className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-[#2D5D9F] focus:outline-none transition-colors text-gray-900 font-medium disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="(305) 813-8377" data-id="element-337" />
                    </div>
                  </div>
                  <div data-id="element-338">
                    <label className="block text-sm font-bold text-[#142B44] mb-3 uppercase tracking-wide" data-id="element-339">
                      Bank Statements *
                    </label>
                    <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200" data-id="element-340">
                      <label className="block mb-4" data-id="element-341">
                        <div className="flex items-center justify-center w-full px-6 py-6 border-2 border-dashed border-[#2D5D9F] rounded-xl cursor-pointer hover:bg-blue-100 transition-colors" data-id="element-342">
                          <div className="text-center" data-id="element-343">
                            <Upload className="mx-auto mb-2 text-[#2D5D9F]" size={32} data-id="element-344" />
                            <span className="text-sm font-semibold text-[#142B44]" data-id="element-345">
                              Choose Files (Up to 3)
                            </span>
                            <p className="text-xs text-gray-600 mt-1" data-id="element-346">
                              PDF or JPG • Max 10 MB each
                            </p>
                          </div>
                        </div>
                        <input type="file" multiple accept=".pdf,.jpg,.jpeg" onChange={handleFileChange} className="hidden" disabled={uploadStatus === 'loading'} data-id="element-347" />
                      </label>
                      {errors.files && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2" data-id="element-348">
                          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} data-id="element-349" />
                          <p className="text-sm text-red-700" data-id="element-350">{errors.files}</p>
                        </div>}
                      {uploadedFiles.length > 0 && <div className="space-y-2" data-id="element-351">
                          {uploadedFiles.map((file, index) => <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200" data-id="element-352">
                              <div className="flex items-center space-x-3" data-id="element-353">
                                <FileText className="text-[#2D5D9F] flex-shrink-0" size={20} data-id="element-354" />
                                <div className="min-w-0" data-id="element-355">
                                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-356">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-500" data-id="element-357">
                                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                                  </p>
                                </div>
                              </div>
                              <button type="button" onClick={() => removeFile(index)} className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" disabled={uploadStatus === 'loading'} data-id="element-358">
                                <X className="text-gray-500" size={18} data-id="element-359" />
                              </button>
                            </div>)}
                        </div>}
                    </div>
                  </div>
                  <button type="submit" disabled={uploadStatus === 'loading'} className="w-full py-5 bg-gradient-to-r from-[#142B44] to-[#2D5D9F] text-white font-bold text-lg rounded-xl hover:from-[#2D5D9F] hover:to-[#142B44] transition-all shadow-xl hover:shadow-2xl hover:scale-105 duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-360">
                    {uploadStatus === 'loading' ? 'Sending...' : 'Submit for Fast-Track Review →'}
                  </button>
                  {uploadStatus === 'error' && <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start space-x-3" data-id="element-361">
                      <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} data-id="element-362" />
                      <div data-id="element-363">
                        <p className="text-sm text-red-800 font-medium leading-relaxed" data-id="element-364">
                          Upload failed. Please try again, or email your
                          statements to{' '}
                          <a href="mailto:antony@sparrowadvance.com" className="underline" data-id="element-365">
                            antony@sparrowadvance.com
                          </a>
                        </p>
                      </div>
                    </div>}
                  <div className="text-center" data-id="element-366">
                    <a href="/" className="inline-flex items-center text-sm text-gray-600 hover:text-[#2D5D9F] transition-colors" data-id="element-367">
                      <ArrowLeft className="mr-2" size={16} data-id="element-368" />
                      Return to Application
                    </a>
                  </div>
                </form>
                <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-369">
                  <p className="text-xs text-gray-600 text-center mb-2" data-id="element-370">
                    Share this upload link:
                  </p>
                  <div className="flex items-center space-x-2" data-id="element-371">
                    <input type="text" readOnly value={prefillUrl} className="flex-1 px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg text-gray-700" data-id="element-372" />
                    <button type="button" onClick={copyToClipboard} className="px-4 py-2 bg-[#142B44] text-white text-xs font-semibold rounded-lg hover:bg-[#2D5D9F] transition-colors" data-id="element-373">
                      Copy
                    </button>
                  </div>
                </div>
              </>}
          </div>
        </div>
      </main>
    </div>;
}